import type AutomatedMessageRuleFilterModel from 'uplisting-frontend/models/automated-message-rule-filter';
import type { GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';

// Booking channels supported for automated message rules (matches
// AutomatedMessageRule::SUPPORTED_CHANNELS in the API). Channel is persisted as
// the rule's `channels` relationship, not as a filter.
export const SUPPORTED_CHANNELS: string[] = [
  'airbnb_official',
  'booking_dot_com',
  'google',
  'home_away',
  'uplisting',
];

export type FilterKind = 'numeric' | 'options' | 'multi';

export const NUMERIC_OPERANDS = ['gt', 'eq', 'lt'];

export interface IFilterOption {
  key: string;
  attribute: string;
  value: string;
}

export interface IFilterType {
  id: string;
  entity: string;
  kind: FilterKind;
  operand: string;
  attribute?: string;
  defaultOperand?: string;
  defaultValue?: string;
  unit?: string;
  options?: IFilterOption[];
  multiOptions?: string[];
  permission?: string;
}

// The catalogue of filter types the API understands
// (lib/uplisting/services/automated_message_rule/filters.rb), with the exact
// {entity, attribute, operand, value} each produces.
export const FILTER_TYPES: IFilterType[] = [
  {
    id: 'duration',
    entity: 'booking',
    attribute: 'duration',
    kind: 'numeric',
    operand: 'gt',
    defaultOperand: 'gt',
    defaultValue: '2',
    unit: 'nights',
  },
  {
    id: 'lead_time',
    entity: 'booking',
    attribute: 'lead_time',
    kind: 'numeric',
    operand: 'lt',
    defaultOperand: 'lt',
    defaultValue: '24',
    unit: 'hours',
  },
  {
    id: 'booking_payment',
    entity: 'booking_payment',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'paid', attribute: 'paid?', value: 'true' },
      { key: 'partially_paid', attribute: 'partially_paid?', value: 'true' },
      { key: 'failed', attribute: 'failed?', value: 'true' },
      { key: 'not_paid', attribute: 'paid?', value: 'false' },
    ],
  },
  {
    id: 'security_deposit',
    entity: 'security_deposit',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'successful', attribute: 'successful?', value: 'true' },
      {
        key: 'needs_payment_information',
        attribute: 'needs_payment_information?',
        value: 'true',
      },
      {
        key: 'has_payment_information',
        attribute: 'has_payment_information?',
        value: 'true',
      },
      { key: 'failed', attribute: 'failed?', value: 'true' },
    ],
  },
  {
    id: 'guest_identity_verification',
    entity: 'guest_identity_verification',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'successful', attribute: 'successful?', value: 'true' },
      { key: 'not_verified', attribute: 'successful?', value: 'false' },
    ],
  },
  {
    id: 'booking_rental_agreement',
    entity: 'booking_rental_agreement',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'successful', attribute: 'successful?', value: 'true' },
      { key: 'not_signed', attribute: 'successful?', value: 'false' },
    ],
  },
  {
    id: 'guest_review',
    entity: 'guest_review',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'left', attribute: 'submitted?', value: 'true' },
      { key: 'not_left', attribute: 'submitted?', value: 'false' },
    ],
  },
  {
    id: 'stay_status',
    entity: 'booking',
    attribute: 'stay_status',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'currently_staying', attribute: 'stay_status', value: 'true' },
      {
        key: 'not_currently_staying',
        attribute: 'stay_status',
        value: 'false',
      },
    ],
  },
  {
    id: 'week_parity',
    entity: 'calendar',
    attribute: 'week_parity',
    kind: 'options',
    operand: 'eq',
    options: [
      { key: 'even', attribute: 'week_parity', value: 'even' },
      { key: 'odd', attribute: 'week_parity', value: 'odd' },
    ],
  },
  {
    id: 'safeguest_status',
    entity: 'booking',
    attribute: 'safeguest_status',
    kind: 'multi',
    operand: 'eq',
    multiOptions: ['green', 'yellow', 'red', 'pending'],
    permission: 'automate.safeguest',
  },
];

// Resolves the catalogue entry for a given (persisted) filter record.
export function filterTypeFor(
  filter: AutomatedMessageRuleFilterModel,
): IFilterType | undefined {
  return FILTER_TYPES.find((type) => {
    if (type.entity !== filter.entity) {
      return false;
    }

    if (type.attribute) {
      return type.attribute === filter.attribute;
    }

    // options-based types match on entity; the attribute identifies the option
    return true;
  });
}

// The default {entity, attribute, operand, value} for a newly-added filter.
export function defaultAttributesFor(type: IFilterType): {
  entity: string;
  attribute: string;
  operand: string;
  value: string;
} {
  if (type.kind === 'numeric') {
    return {
      entity: type.entity,
      attribute: type.attribute as string,
      operand: type.defaultOperand as string,
      value: type.defaultValue as string,
    };
  }

  if (type.kind === 'options') {
    const option = type.options?.[0] as IFilterOption;

    return {
      entity: type.entity,
      attribute: option.attribute,
      operand: type.operand,
      value: option.value,
    };
  }

  // multi (safeguest): value is a comma-separated list, empty until chosen
  return {
    entity: type.entity,
    attribute: type.attribute as string,
    operand: type.operand,
    value: '',
  };
}

export function isAutomatedMessageRuleFilterValid(
  filter: AutomatedMessageRuleFilterModel,
): boolean {
  const type = filterTypeFor(filter);

  if (!type) {
    return false;
  }

  if (type.kind === 'numeric') {
    const value = Number(filter.value);

    return Number.isInteger(value) && value > 0;
  }

  if (type.kind === 'options') {
    return !!type.options?.some(
      (option) =>
        option.attribute === filter.attribute && option.value === filter.value,
    );
  }

  const selectedValues = filter.value
    .split(',')
    .filter((value) => value.length > 0);

  return (
    selectedValues.length > 0 &&
    selectedValues.every((value) => type.multiOptions?.includes(value))
  );
}

export function areAutomatedMessageRuleFiltersValid(
  filters: AutomatedMessageRuleFilterModel[],
): boolean {
  return filters.every(isAutomatedMessageRuleFilterValid);
}

// A rule is saveable/advanceable when its attributes pass validation, at
// least one channel is selected, and every filter is fully specified.
// Callers must run changeset.validate() first for isInvalid to be current.
export function isAutomatedMessageRuleIncomplete(
  changeset: GenericChangeset<ModelRegistry['automated-message-rule']>,
): boolean {
  return (
    changeset.isInvalid ||
    !changeset.channels.length ||
    !areAutomatedMessageRuleFiltersValid(changeset.automatedMessageRuleFilters)
  );
}
